/**
 * ScrolledUpButton — round floating button (bottom-right of the message list)
 * that scrolls the chat back to the most recent message.
 *
 * Flutter source of truth: `ScrolledUpWidget`
 * (`lib/presentation/custom_widgets/scrolled_up_widget.dart`). The chat
 * screen mounts this widget at `chat_screen.dart:3446-3453` with:
 *   - `backgroundColor: AppColors.transparent`
 *   - `iconBackgroundColor: AppColors.backgroundColorLight`
 *   - `iconColor: AppColors.disabledTextColor`
 *   - `buttonSize: DimensionConstants.scrolledUpIconSize` (= 48)
 *   - `iconSize: .6 * DimensionConstants.scrolledUpIconSize` (= 28.8)
 *
 * Visually: a circular surface containing a centered down-chevron. The Flutter
 * implementation reuses the project's `arrow_up.png` asset and applies a
 * 180° (`-pi`) rotation to point it down (see
 * `scrolled_up_widget.dart:38-39`). We do the same on web via
 * `ANIMATION_CONSTANTS.rotation.down`, so we don't have to ship a second asset.
 *
 * Tap behavior: invokes `onClick`. The caller (Task 35 `ChatPage`) wires this
 * to the message-list "scroll to bottom" handler exposed by `useChat`.
 *
 * Deviations from Flutter UI (allowed per `presentation.md`):
 *  - `buttonSize` maps to `responsive.button.size.large` (= 48 on mobile —
 *    exact parity with Flutter's `scrolledUpIconSize`) instead of inlining a
 *    literal.
 *  - `iconSize` maps to `responsive.icon.xlarge` (= 32 on mobile). Flutter
 *    uses `.6 * 48 = 28.8`; the closest responsive token is `xlarge`. This
 *    keeps the icon on the project's responsive scale per `presentation.md`.
 *  - Icon background color uses `theme.secondary` and the inner chevron uses
 *    `theme.text.primary` for consistency with the rest of the chat chrome
 *    (composer, headers, `UnreadBanner` pill). This is a deliberate web-only
 *    deviation from Flutter's `AppColors.backgroundColorLight` /
 *    `AppColors.disabledTextColor` — the project tokens keep theme-mode
 *    parity (light/dark) and align the floating button with the surrounding
 *    chat UI rather than the Flutter palette.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ScrolledUpButtonProps {
  /**
   * Invoked when the user taps the button. The host wires this to the
   * message-list scroll-to-bottom action (Flutter `_scrollDown`,
   * `chat_screen.dart:3452`).
   */
  onClick: () => void;
}

export const ScrolledUpButton: React.FC<ScrolledUpButtonProps> = ({
  onClick,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const buttonSize = responsive.button.size.large;
  const iconSize = responsive.icon.xlarge;

  return (
    <button
      type="button"
      {...qaAttr({ id: 'chat-scroll-to-bottom' })}
      onClick={onClick}
      style={{
        width: buttonSize,
        height: buttonSize,
        // Flutter `BorderRadius.circular(buttonSize)` —
        // `scrolled_up_widget.dart:35`. A radius >= half the side renders a
        // perfect circle; the responsive `round` token is `50%`.
        borderRadius: responsive.borderRadius.round,
        backgroundColor: theme.secondary,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}
    >
      <AssetIcon
        // Reuse the existing arrow-up asset rotated 180° — matches
        // Flutter `Transform.rotate(angle: -pi)` at
        // `scrolled_up_widget.dart:38-39`. Avoids shipping a duplicate asset.
        path="/assets/images/arrow_up.png"
        width={iconSize}
        height={iconSize}
        color={theme.text.primary}
        rotationAngle={ANIMATION_CONSTANTS.rotation.down}
      />
    </button>
  );
};
